import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import {
  Box,
  Stack,
  Button,
  SubmitButton,
  Image,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import {
  PencilIcon,
  FloppyIcon,
  ExpandLessIcon,
} from '@fortress-technology-solutions/fortress-component-library/Icons';
import { Grid } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import { isEmpty, pathOr, propOr, uniqBy } from 'ramda';
import DocumentTitle from 'react-document-title';
import { FormattedMessage, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import ElementWithPermissions from '../../components/ElementWithPermissions';
import SocketService from '../../services/socketService';
import {
  promptToaster,
  removeToaster,
  selectProperty,
  updateCurrentUserSelectedProperty,
} from '../App/actions';
import { useAsyncStates } from '../ManageBuildings/hooks';
import AffordableSetup from './AffordableSetup';
import { buildWaitlistV2Payload } from './AffordableSetup/WaitlistV2/utils';
import { BankInformationModal } from './BankInformationModal';
import BasicDetails from './BasicDetails';
import {
  useAsyncMarketingSection,
  useAsyncMasterLeaseTerms,
  useAsyncPropertyDetails,
  useAsyncProrateMethods,
  useAsyncValidateFirstOfMonthPostEnabled,
  useForm,
  usePostFirstOfMonthMLT,
  usePropertyRoles,
  useMoveOutProrateMethods,
  useOrgSecondaryPropertyTypes,
  useIsFormInvalid,
  hasNewHUDContractNumber,
} from './hooks';
import AddContractConfirmationModal from './AffordableSetup/Programs/HUD/AddContractConfirmationModal';
import { useSecurityDepositInterestRates } from './LeaseSetup/SecurityDepositInterestTable/hooks';
import IntegrationsSetup from './IntegrationsSetup';
import InsuranceSetup from './InsuranceSetup';
import LeaseSetup from './LeaseSetup';
import MarketingDetails from './MarketingDetails';
import messages from './messages';
import MobileAppSetup from './MobileAppSetup';
import TwoWayTextingSetup from './TwoWayTextingSetup';
import {
  getEquivalentDeprecatedMoveOutProrateMethodId,
  getEquivalentMoveOutProrateMethodId,
  mapScreeningSetupDataToSubmitPayload,
  camelCaseToWords,
  cleanData,
} from './utils';
import { v4 as uuid } from 'uuid';
import {
  MINIMUM_RENT_IN_PROGRESS_TOAST_ID,
  MINIMUM_RENT_RECALC_TIMEOUT_MS,
} from './constants';
import {
  registerMinimumRentFallback,
  clearMinimumRentFallback,
} from './minimumRentFallback';

export { MINIMUM_RENT_IN_PROGRESS_TOAST_ID };

type Props = {
  history: Object,
  intl: any,
  match: Object,
  organizationId: any,
  userOrganization: any,
  actions: Object,
  userPermissions: any[],
  selectedProperty: Object,
  userId: string,
  currentUserOrg: Object,
  locale: string,
};

export const normalizeHUDContractNumber = (contractNumber) => {
  if (typeof contractNumber !== 'string') return contractNumber ?? null;
  const trimmed = contractNumber.trim();
  return trimmed.length > 0 ? trimmed : null;
};

export const normalizeHUDContractMinimumRent = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const coerced = Number(value);
  return Number.isInteger(coerced) ? coerced : null;
};

// Sent only when edited, so an untouched legacy value (e.g. "TBD") never fails an unrelated save.
export const editedSetupFieldPatch = (field, value, original) =>
  (value ?? null) === (original ?? null)
    ? {}
    : { [field]: value?.trim() || null };

// A whitelist rather than a spread. The property read returns server-derived fields on each
// contract (`assignedUnitCount`), and the save schema is a plain Joi object that rejects unknown
// keys — so anything not listed here must not round-trip. Keeping it a whitelist means a read-only
// field added to the read later is excluded automatically instead of breaking the save. `id` is the
// one that has to round-trip: without it the server falls back to matching on the contract number
// itself, so an edited number arrives as a delete plus a create and unassigns every unit on it.
export const normalizeHUDContractForSubmit = (contract) => ({
  id: contract?.id ?? null,
  contractNumber: normalizeHUDContractNumber(contract?.contractNumber),
  eligibilityUniverseCode: contract?.eligibilityUniverseCode ?? null,
  effectiveDate: contract?.effectiveDate ?? null,
  expirationDate: contract?.expirationDate ?? null,
  minimumRent: normalizeHUDContractMinimumRent(contract?.minimumRent),
  ownerDUNSNumber: contract?.ownerDUNSNumber || null,
  parentCompanyDUNSNumber: contract?.parentCompanyDUNSNumber || null,
  ownerTIN: contract?.ownerTIN || null,
  parentCompanyTIN: contract?.parentCompanyTIN || null,
});

export const hasHUDContractData = (contract) => {
  const normalized = normalizeHUDContractForSubmit(contract);
  return Boolean(
    normalized?.contractNumber ||
      normalized?.eligibilityUniverseCode ||
      normalized?.effectiveDate ||
      normalized?.expirationDate ||
      normalized?.minimumRent !== null,
  );
};

// Keyed by persisted id only. A row without one is a contract the user just added, and the server
// decides create-vs-update on that same id, so it cannot produce a recalc however it is filled in.
const buildMinimumRentMap = (setup) => {
  const contracts = [
    ...(setup?.primaryContracts ?? []),
    ...(setup?.secondaryContracts ?? []),
  ];
  return contracts.reduce((acc, contract) => {
    if (!contract?.id) return acc;
    acc.set(
      contract.id,
      normalizeHUDContractMinimumRent(contract?.minimumRent),
    );
    return acc;
  }, new Map());
};

/**
 * Whether this save will make the server recalculate open HUD certifications, which is what decides
 * if the screen waits for a `hud.minRent.*` socket notification instead of confirming the save
 * itself. It therefore has to answer the question the same way the server does, not merely a
 * reasonable version of it: a false positive leaves the in-progress toast waiting ~150s for an event
 * nobody will send.
 *
 * The server compares `minimumTenantContribution` per funding id and ignores anything it cannot
 * match to an existing row, so adding or removing a contract never triggers a recalc on its own — a
 * new row has no id yet, and a removed one is absent from the payload it reduces over. Only a
 * changed value on a contract that existed before counts, which is exactly what this checks.
 */
export const didMinimumRentChange = (prevSetup, nextSetup) => {
  const prevMap = buildMinimumRentMap(prevSetup);
  const nextMap = buildMinimumRentMap(nextSetup);

  return [...nextMap.keys()].some(
    (id) => prevMap.has(id) && prevMap.get(id) !== nextMap.get(id),
  );
};

export const ViewProperty = (props: Props) => {
  const {
    intl,
    history,
    organizationId,
    userOrganization,
    actions,
    match,
    userPermissions,
    selectedProperty,
    userId,
    currentUserOrg,
    locale,
    selectedPropertyTransactionCodes,
  } = props;

  const propertyId = pathOr(
    propOr('', 'id', selectedProperty),
    ['params', 'propertyId'],
    match,
  );
  const defaultCollectionAgency = currentUserOrg?.defaultCollectionAgencyId;

  const isPropertyHomeTab = match?.params?.propertyId === undefined;

  const orgPropUrl = `${organizationId}/${propertyId}`;

  const hasPropertyUpdatePermission = userPermissions.find(
    (permission) => permission.scope === 'property-update',
  );

  const { states } = useAsyncStates();
  const [prorateMethods] = useAsyncProrateMethods();
  const moveOutProrateMethods = useMoveOutProrateMethods();
  const [masterLeaseTerms] = useAsyncMasterLeaseTerms(organizationId);
  const [leaseTerms, setLeaseTerms] = useState([]);
  const propertyLeaseTermsRef = useRef([]);
  const minimumRentFallbackRef = useRef(null);
  const [showBankInformationModal, setShowBankInformationModal] =
    useState(false);
  const [showAddContractConfirmation, setShowAddContractConfirmation] =
    useState(false);

  const [
    propertyDetails,
    updateProperty,
    updateIntegrations,
    propertyDetailsIsLoading,
  ] = useAsyncPropertyDetails(
    organizationId,
    propertyId,
    intl,
    actions.selectProperty,
    actions.updateCurrentUserSelectedProperty,
  );

  const [marketingInfo, updateMarketingSection, marketingSectionIsLoading] =
    useAsyncMarketingSection(organizationId, propertyId);

  const [marketingData, setMarketingData, onMarketingDataChange] =
    useForm(marketingInfo);
  const [propertyData, setPropertyData, onPropertyDataChange] =
    useForm(propertyDetails);

  const [isMltStatusLoading, enabledStatusMLT, errorMLTValidation] =
    useAsyncValidateFirstOfMonthPostEnabled({ organizationId, propertyId });

  const [postMLT, isPostingMLT] = usePostFirstOfMonthMLT(
    intl,
    propertyId,
    organizationId,
    actions.promptToaster,
  );
  const isAffordableSetupShown = useMemo(
    () =>
      ['Affordable', 'Mixed'].includes(propertyDetails?.propertyClass ?? ''),
    [propertyDetails],
  );

  const affordableDetails = useMemo(
    () => ({
      programs: propertyDetails?.pap ?? [],
      hudSubsidyType: propertyData?.setup?.hudPrimarySubsidyType?.name,
      hudSecondarySubsidyType:
        propertyData?.setup?.hudSecondarySubsidyType?.name,
      organizationId,
      propertyId,
      state: propertyData?.state,
    }),
    [propertyDetails, organizationId, propertyId, propertyData],
  );

  const affordableSetupValues = useMemo(
    () => ({
      stateId: propertyData?.setup?.stateId,
      projectIdTic: propertyData?.setup?.projectIdTic,
      primaryContracts: propertyData?.setup?.primaryContracts,
      secondaryContracts: propertyData?.setup?.secondaryContracts,
      HUDContractNumber: propertyData?.setup?.HUDContractNumber,
      LIHTCContractEffectiveDate:
        propertyData?.setup?.LIHTCContractEffectiveDate,
      LIHTCContractExpirationDate:
        propertyData?.setup?.LIHTCContractExpirationDate,
      RDContractEffectiveDate: propertyData?.setup?.RDContractEffectiveDate,
      RDContractExpirationDate: propertyData?.setup?.RDContractExpirationDate,
      HUDProjectNumber: propertyData?.setup?.HUDProjectNumber,
      HUDiMaxNumber: propertyData?.setup?.HUDiMaxNumber,
      HUDPropertyId: propertyData?.setup?.HUDPropertyId,
      HUDRepaymentCollectionFeePercentage:
        propertyData?.setup?.HUDRepaymentCollectionFeePercentage ?? 20,
      requiresInitialCertificationForNonHUD: {
        LIHTC:
          propertyData?.setup?.requiresInitialCertificationForNonHUD ?? false,
        NOAH: propertyData?.setup?.requiresInitialCertificationForNOAH ?? false,
        DeferredLoan:
          propertyData?.setup?.requiresInitialCertificationForDeferredLoan ??
          false,
        VHCB: propertyData?.setup?.requiresInitialCertificationForVHCB ?? false,
        NSP: propertyData?.setup?.requiresInitialCertificationForNSP ?? false,
        AHP: propertyData?.setup?.requiresInitialCertificationForAHP ?? false,
        KCMO: propertyData?.setup?.requiresInitialCertificationForKCMO ?? false,
        StLouisFunds:
          propertyData?.setup?.requiresInitialCertificationForStLouisFunds ??
          false,
        TCAP: propertyData?.setup?.requiresInitialCertificationForTCAP ?? false,
        BOND: propertyData?.setup?.requiresInitialCertificationForBOND ?? false,
      },
      XMLPropertyName: propertyData?.setup?.XMLPropertyName,
      XMLProjectName: propertyData?.setup?.XMLProjectName,
      keyNumber: propertyData?.setup?.keyNumber,
      isOverrideQualificationEnabled: {
        LIHTC: propertyData?.setup?.isOverrideQualificationEnabled,
        NOAH: propertyData?.setup?.isOverrideQualificationEnabledNOAH,
        DeferredLoan:
          propertyData?.setup?.isOverrideQualificationEnabledDeferredLoan,
        VHCB: propertyData?.setup?.isOverrideQualificationEnabledVHCB,
        NSP: propertyData?.setup?.isOverrideQualificationEnabledNSP,
        AHP: propertyData?.setup?.isOverrideQualificationEnabledAHP,
        KCMO: propertyData?.setup?.isOverrideQualificationEnabledKCMO,
        StLouisFunds:
          propertyData?.setup?.isOverrideQualificationEnabledStLouisFunds,
        TCAP: propertyData?.setup?.isOverrideQualificationEnabledTCAP,
        BOND: propertyData?.setup?.isOverrideQualificationEnabledBOND,
      },
      RDProjectNumber: propertyData?.setup?.RDProjectNumber,
      RDProgramType: propertyData?.setup?.rdProgramType,
      RDProjectName: propertyData?.setup?.RDProjectName,
      RDBorrowerId: propertyData?.setup?.RDBorrowerId,
      RDProjectType: propertyData?.setup?.rdProjectType,
      RDProjectRentalTypes: propertyData?.setup?.RDProjectRentalTypes ?? [],
      RDNYStateContractNumber: propertyData?.setup?.RDNYStateContractNumber,
      isRDRAEnabled: propertyData?.setup?.isRDRAEnabled,
      RDTotalRAUnits: propertyData?.setup?.RDTotalRAUnits,
      loanTransferAmount: propertyData?.setup?.loanTransferAmount,
      noteRentPayment: propertyData?.setup?.noteRentPayment,
      icPayment: propertyData?.setup?.icPayment,
      RD538Loan: propertyData?.setup?.RD538Loan,
      hudPrimarySubsidyType: propertyData?.setup?.hudPrimarySubsidyType,
      hudSecondarySubsidyType: propertyData?.setup?.hudSecondarySubsidyType,
      HUDCompleteGRCertsWithoutSigned59As:
        propertyData?.setup?.HUDCompleteGRCertsWithoutSigned59As,
      HOMEUnitType: propertyData?.setup?.HOMEUnitType,
      HOMEAnnualIncomeType: propertyData?.setup?.HOMEAnnualIncomeType,
      HOMEPropertyType: propertyData?.setup?.HOMEPropertyType,
      HTFUnitType: propertyData?.setup?.HTFUnitType,
      HTFAffordabilityPeriodStartDate:
        propertyData?.setup?.HTFAffordabilityPeriodStartDate,
      autoSendToTRACS: propertyData?.config?.autoSendToTRACS,
      timezone: propertyData?.config?.timezone,
      reserveAccountRequiredBalance:
        propertyData?.setup?.reserveAccountRequiredBalance,
      agencyName: propertyData?.setup?.agencyName,
      phaCode: propertyData?.setup?.phaCode,
      PHProgramType: propertyData?.setup?.PHProgramType,
      PHProjectNumber: propertyData?.setup?.PHProjectNumber,
      PHProjectSuffix: propertyData?.setup?.PHProjectSuffix,
      phProjects: propertyData?.setup?.phProjects ?? [],
      PHMinimumRent: propertyData?.setup?.PHMinimumRent,
      autoSendToPIC: propertyData?.setup?.autoSendToPIC,
      autoRolloverIncomeAndAssets: {
        HUD: propertyData?.setup?.HUDAutoRolloverIncomeAndAssets,
        LIHTC: propertyData?.setup?.LIHTCAutoRolloverIncomeAndAssets,
        HOME: propertyData?.setup?.HOMEAutoRolloverIncomeAndAssets,
        RD: propertyData?.setup?.RDAutoRolloverIncomeAndAssets,
        NOAH: propertyData?.setup?.NOAHAutoRolloverIncomeAndAssets,
        DeferredLoan:
          propertyData?.setup?.DeferredLoanAutoRolloverIncomeAndAssets,
        VHCB: propertyData?.setup?.VHCBAutoRolloverIncomeAndAssets,
        NSP: propertyData?.setup?.NSPAutoRolloverIncomeAndAssets,
        AHP: propertyData?.setup?.AHPAutoRolloverIncomeAndAssets,
        KCMO: propertyData?.setup?.KCMOAutoRolloverIncomeAndAssets,
        StLouisFunds:
          propertyData?.setup?.StLouisFundsAutoRolloverIncomeAndAssets,
        HTF: propertyData?.setup?.HTFAutoRolloverIncomeAndAssets,
        TCAP: propertyData?.setup?.TCAPAutoRolloverIncomeAndAssets,
        PH: propertyData?.setup?.PHAutoRolloverIncomeAndAssets,
        BOND: propertyData?.setup?.BONDAutoRolloverIncomeAndAssets,
      },
      interimCert: {
        LIHTC: propertyData?.setup?.interimCert,
        NOAH: propertyData?.setup?.interimCertNOAH,
        DeferredLoan: propertyData?.setup?.interimCertDeferredLoan,
        VHCB: propertyData?.setup?.interimCertVHCB,
        NSP: propertyData?.setup?.interimCertNSP,
        AHP: propertyData?.setup?.interimCertAHP,
        KCMO: propertyData?.setup?.interimCertKCMO,
        StLouisFunds: propertyData?.setup?.interimCertStLouisFunds,
        TCAP: propertyData?.setup?.interimCertTCAP,
        BOND: propertyData?.setup?.interimCertBOND,
      },
      annualRecertMonth: {
        HUD: propertyData?.setup?.HUDAnnualRecertMonth,
        LIHTC: propertyData?.setup?.LIHTCAnnualRecertMonth,
        HOME: propertyData?.setup?.HOMEAnnualRecertMonth,
        RD: propertyData?.setup?.RDAnnualRecertMonth,
        NOAH: propertyData?.setup?.NOAHAnnualRecertMonth,
        DeferredLoan: propertyData?.setup?.DeferredLoanAnnualRecertMonth,
        VHCB: propertyData?.setup?.VHCBAnnualRecertMonth,
        NSP: propertyData?.setup?.NSPAnnualRecertMonth,
        AHP: propertyData?.setup?.AHPAnnualRecertMonth,
        KCMO: propertyData?.setup?.KCMOAnnualRecertMonth,
        StLouisFunds: propertyData?.setup?.StLouisFundsAnnualRecertMonth,
        HTF: propertyData?.setup?.HTFAnnualRecertMonth,
        TCAP: propertyData?.setup?.TCAPAnnualRecertMonth,
        PH: propertyData?.setup?.PHAnnualRecertMonth,
        BOND: propertyData?.setup?.BONDAnnualRecertMonth,
      },
      certificationPastDueDefinition: {
        HUD: propertyData?.setup?.HUDCertificationPastDueDefinition,
        LIHTC: propertyData?.setup?.LIHTCCertificationPastDueDefinition,
        RD: propertyData?.setup?.RDCertificationPastDueDefinition,
        NOAH: propertyData?.setup?.NOAHCertificationPastDueDefinition,
        DeferredLoan:
          propertyData?.setup?.DeferredLoanCertificationPastDueDefinition,
        VHCB: propertyData?.setup?.VHCBCertificationPastDueDefinition,
        AHP: propertyData?.setup?.AHPCertificationPastDueDefinition,
        NSP: propertyData?.setup?.NSPCertificationPastDueDefinition,
        KCMO: propertyData?.setup?.KCMOCertificationPastDueDefinition,
        StLouisFunds:
          propertyData?.setup?.StLouisFundsCertificationPastDueDefinition,
        TCAP: propertyData?.setup?.TCAPCertificationPastDueDefinition,
        HOME: propertyData?.setup?.HOMECertificationPastDueDefinition,
        HTF: propertyData?.setup?.HTFCertificationPastDueDefinition,
        PH: propertyData?.setup?.PHCertificationPastDueDefinition,
        BOND: propertyData?.setup?.BONDCertificationPastDueDefinition,
      },
      affordableWaitlist: {
        enabled: propertyData?.setup?.affordableWaitlist,
        status: propertyData?.setup?.affordableWaitlistStatus,
        programs: propertyData?.setup?.waitlistV2?.programs ?? null,
        openDate: propertyData?.setup?.waitlistV2?.scheduledOpenDate ?? null,
        closeDate: propertyData?.setup?.waitlistV2?.scheduledCloseDate ?? null,
        separateByBedroom:
          propertyData?.setup?.waitlistV2?.separateByBedroom ?? false,
        usePreferences:
          propertyData?.setup?.waitlistV2?.usePreferencesForRanking ?? false,
        preferenceWeightMethod:
          propertyData?.setup?.waitlistV2?.preferenceWeightMethod ?? null,
        preferences: propertyData?.setup?.waitlistV2?.preferences ?? [],
      },
      rdManagementFee: {
        nonContinuousParcelsEnabled:
          propertyData?.setup?.RDNonContinuousParcelsEnabled,
        propertiesInRemoteEnabled: propertyData?.setup?.RDRemoteLocationEnabled,
        troubledOrCriticalEnabled: propertyData?.setup?.RDTroubledOrCritical,
      },
    }),
    [propertyData],
  );

  const {
    riskMgmtAssetProtectAdmin,
    mobileCommunicationsToResidents,
    passbookRate,
    propertyRoleAssignments,
    prorateRentsForMoveOuts,
    leaseAppRevampTransunion,
    leaseAppRevampTruework,
    propertyAmenities,
    devPhaseDropDown,
    accrueSecurityDepositInterest: accrueSecurityDepositInterestFlag,
  } = useFlags();
  const isNewServiceOn = leaseAppRevampTransunion || leaseAppRevampTruework;

  const hasWorkOrderMobileFeature = useMemo(() => {
    if (propertyDetailsIsLoading) return false;
    return (
      propertyDetails.hubPremiumFeatures.find(
        (f) => f.name === 'workOrders',
      ) !== undefined
    );
  }, [propertyDetails, propertyDetailsIsLoading]);
  const showMobileAppSetupPanel =
    hasWorkOrderMobileFeature && mobileCommunicationsToResidents;

  const ldClient = useLDClient();

  const {
    propertyRoles,
    roleAssignments,
    setRoleAssignments,
    handleAssignmentChange,
    assignPropertyRoles,
  } = usePropertyRoles({ organizationId, propertyId });

  const {
    secondaryPropertyTypes,
    secondaryPropertyType,
    handleSecondaryPropertyTypeChange,
  } = useOrgSecondaryPropertyTypes(
    organizationId,
    propertyDetails.secondaryPropertyTypeId,
  );
  const {
    securityDepositInterestRates,
    setSecurityDepositInterestRates,
    updateSecurityDepositInterestRates,
    editableRates,
    modifiedRates,
    setModifiedRates,
    newInterestRatesToCreate,
    setNewInterestRatesToCreate,
    interestRatesAreValid,
    securityDepositInterestRatesIsLoading,
  } = useSecurityDepositInterestRates({
    organizationId,
    propertyId,
  });

  useEffect(
    () => () => {
      if (minimumRentFallbackRef.current) {
        clearTimeout(minimumRentFallbackRef.current);
        minimumRentFallbackRef.current = null;
      }
      clearMinimumRentFallback(propertyId);
    },
    [propertyId],
  );

  useEffect(() => {
    setMarketingData(marketingInfo);
  }, [marketingInfo, setMarketingData]);
  useEffect(() => {
    setPropertyData(propertyDetails);
  }, [propertyDetails, setPropertyData]);
  useEffect(() => {
    propertyLeaseTermsRef.current = uniqBy(
      (lt) => lt.label,
      propertyDetails.propertyLeaseTerms.map((propertyLeaseTerm) => ({
        value: propertyLeaseTerm.id,
        label: propertyLeaseTerm.masterLeaseTerm.nMonths,
      })),
    );
    setLeaseTerms(propertyLeaseTermsRef.current);
  }, [propertyDetails]);

  useEffect(() => {
    ldClient.identify({
      kind: 'user',
      key: userId,
      hostname: window.location.hostname,
      propertyId,
      organizationId,
    });

    const onMLTPostingSocketResponse = (message) => {
      actions.promptToaster({
        type: message.status || 'error',
        title: 'MLT posting result',
        message: message.text,
      });
    };

    if (SocketService && SocketService.socket) {
      if (SocketService.socket) {
        SocketService.socket.off('mmlt.success');
        SocketService.socket.off('mmlt.error');
      }
      SocketService.socket.on('mmlt.success', onMLTPostingSocketResponse);
      SocketService.socket.on('mmlt.error', onMLTPostingSocketResponse);
    }

    return () => {
      if (SocketService.socket) {
        SocketService.socket.off('mmlt.success');
        SocketService.socket.off('mmlt.error');
      }
    };
  }, [propertyId]); // eslint-disable-line

  const [editMode, setEditMode] = useState(false);

  const [submit, setSubmit] = useState(false);

  const arePropertiesEmpty = (data: Object, ignoreKeys: string[] = []) => {
    const emptyProperties = Object.entries(data).reduce(
      (acc, [key, value]: any) => {
        if (!ignoreKeys.includes(key) && isEmpty(value)) {
          acc.push(key);
        }
        return acc;
      },
      [],
    );

    if (emptyProperties.length > 0) {
      actions.promptToaster({
        type: 'error',
        title: intl.formatMessage(messages.error),
        message: intl.formatMessage(
          {
            id: 'error.fillInValues',
            defaultMessage: 'Please fill in the following values: {values}',
          },
          { values: emptyProperties.map(camelCaseToWords).join(', ') },
        ),
      });
      return true;
    }
    return false;
  };
  const [
    masterMeteredUtilitiesOtherSelected,
    setMasterMeteredUtilitiesOtherSelected,
  ] = useState(false);

  const onEdit = async () => {
    if (editMode) {
      const { setup, config } = propertyData;
      const isAsyncMinRecalc = didMinimumRentChange(
        propertyDetails.setup,
        setup,
      );
      const minRentInProgressToastId = `${MINIMUM_RENT_IN_PROGRESS_TOAST_ID}:${propertyId}`;
      const { timezone, ...restOfConfig } = config;
      const munibillingSettingSync =
        propertyData.isMunibillingEnabled?.value !== undefined
          ? propertyData.isMunibillingEnabled.value
          : propertyData.propertyIntegrations.find(
              (i) => i.integration?.name === 'Munibilling',
            )?.isActive;

      let deprecated_moveOutProrateMethodId =
        propertyData.deprecated_moveOutProrateMethodId;
      let moveOutProrateMethodId = propertyData.moveOutProrateMethodId;

      if (prorateRentsForMoveOuts) {
        deprecated_moveOutProrateMethodId =
          getEquivalentDeprecatedMoveOutProrateMethodId(
            propertyData.moveOutProrateMethodId,
            prorateMethods,
            moveOutProrateMethods,
          );
      } else {
        moveOutProrateMethodId = getEquivalentMoveOutProrateMethodId(
          propertyData.deprecated_moveOutProrateMethodId,
          prorateMethods,
          moveOutProrateMethods,
        );
      }

      try {
        const screeningSubmitPayload =
          mapScreeningSetupDataToSubmitPayload(
            propertyData.screeningSetupData ?? {},
          ) ?? {};

        const data = {
          marketing: {
            pitch1: cleanData(marketingData, 'pitch1'),
            officeHoursDescription: cleanData(
              marketingData,
              'officeHoursDescription',
            ),
            propertyDescription: cleanData(
              marketingData,
              'propertyDescription',
            ),
            petPolicyDescription: cleanData(
              marketingData,
              'petPolicyDescription',
            ),
            parkingDetailDescription: cleanData(
              marketingData,
              'parkingDetailDescription',
            ),
            ...(propertyAmenities
              ? { amenities: marketingData.amenities ?? [] }
              : {}),
          },
          property: {
            name: cleanData(propertyData, 'name'),
            propertyEmail: cleanData(propertyData, 'email'),
            phoneNumber: propertyData.phoneNumber || '',
            faxNumber: propertyData.fax || '',
            physicalStreetAddress1: cleanData(propertyData, 'address1'),
            physicalStreetAddress2: cleanData(propertyData, 'address2'),
            physicalState: propertyData.state,
            physicalCity: cleanData(propertyData, 'city'),
            physicalZip: propertyData.zip,
            leaseRentPercentage: cleanData(propertyData, 'leaseRentPercentage'),
            sitePetWeightMax: cleanData(propertyData, 'sitePetWeightMax'),
            turnMoveOutDays: propertyData.turnMoveOutDays,
            leaseTerms,
            cashPrepaidGLCode: cleanData(propertyData, 'cashPrepaidGLCode'),
            applicationIncomeMultiplier: cleanData(
              propertyData,
              'applicationIncomeMultiplier',
            ),
            isDomusoActive: propertyData.isDomusoActive,
            // Keeping both permissions in sync, can be cleaned up after deprecating old method
            isMunibillingEnabled: munibillingSettingSync,
            isTransUnionActive: propertyData.isTransUnionActive,
            domusoPropertyCode: cleanData(propertyData, 'domusoPropertyCode'),
            renewalProrateMethodId: propertyData.renewalProrateMethodId,
            moveOutProrateMethodId,
            deprecated_moveOutProrateMethodId,
            quoteExpTime: propertyData.quoteExpTime,
            isNoEndDateActive: propertyData.isNoEndDateActive,
            leaseEndDateGreaterThan12m: propertyData.leaseEndDateGreaterThan12m,
            leaseEndDateLesserThan12m: propertyData.leaseEndDateLesserThan12m,
            config: restOfConfig,
            // With Waitlist V2 on, the property read returns a waitlist-only setup
            // with no id for a property that has no AffordableSetup row. The save
            // rejects a setup without an id, so it is left out entirely.
            setup: setup?.id
              ? {
                  id: setup.id,
                  stateId: setup.stateId,
                  projectIdTic: setup.projectIdTic,
                  primaryContracts: (setup.primaryContracts ?? [])
                    .map(normalizeHUDContractForSubmit)
                    .filter(hasHUDContractData),
                  secondaryContracts: (setup.secondaryContracts ?? [])
                    .map(normalizeHUDContractForSubmit)
                    .filter(hasHUDContractData),
                  HUDContractNumber: setup.HUDContractNumber,
                  HUDSecondaryContractNumber: setup.HUDSecondaryContractNumber,
                  HUDContractEffectiveDate: setup.HUDContractEffectiveDate,
                  HUDContractExpirationDate: setup.HUDContractExpirationDate,
                  HUDContractEligibilityUniverseCode:
                    setup.HUDContractEligibilityUniverseCode,
                  HUDSecondaryContractEffectiveDate:
                    setup.HUDSecondaryContractEffectiveDate,
                  HUDSecondaryContractExpirationDate:
                    setup.HUDSecondaryContractExpirationDate,
                  HUDSecondaryContractEligibilityUniverseCode:
                    setup.HUDSecondaryContractEligibilityUniverseCode,
                  LIHTCContractEffectiveDate: setup.LIHTCContractEffectiveDate,
                  LIHTCContractExpirationDate:
                    setup.LIHTCContractExpirationDate,
                  RDContractEffectiveDate: setup.RDContractEffectiveDate,
                  RDContractExpirationDate: setup.RDContractExpirationDate,
                  RDNYStateContractNumber: setup.RDNYStateContractNumber,
                  RDProgramTypeId: propertyDetails?.setup?.rdProgramType?.id,
                  ...editedSetupFieldPatch(
                    'HUDProjectNumber',
                    setup.HUDProjectNumber,
                    propertyDetails?.setup?.HUDProjectNumber,
                  ),
                  ...editedSetupFieldPatch(
                    'HUDPropertyId',
                    setup.HUDPropertyId,
                    propertyDetails?.setup?.HUDPropertyId,
                  ),
                  ...editedSetupFieldPatch(
                    'HUDiMaxNumber',
                    setup.HUDiMaxNumber,
                    propertyDetails?.setup?.HUDiMaxNumber,
                  ),
                  HUDRepaymentCollectionFeePercentage:
                    setup.HUDRepaymentCollectionFeePercentage,
                  requiresInitialCertificationForNonHUD:
                    setup.requiresInitialCertificationForNonHUD,
                  requiresInitialCertificationForNOAH:
                    setup.requiresInitialCertificationForNOAH,
                  requiresInitialCertificationForDeferredLoan:
                    setup.requiresInitialCertificationForDeferredLoan,
                  requiresInitialCertificationForVHCB:
                    setup.requiresInitialCertificationForVHCB,
                  requiresInitialCertificationForNSP:
                    setup.requiresInitialCertificationForNSP,
                  requiresInitialCertificationForAHP:
                    setup.requiresInitialCertificationForAHP,
                  requiresInitialCertificationForKCMO:
                    setup.requiresInitialCertificationForKCMO,
                  requiresInitialCertificationForStLouisFunds:
                    setup.requiresInitialCertificationForStLouisFunds,
                  requiresInitialCertificationForTCAP:
                    setup.requiresInitialCertificationForTCAP,
                  requiresInitialCertificationForBOND:
                    setup.requiresInitialCertificationForBOND,
                  XMLPropertyName: setup.XMLPropertyName,
                  XMLProjectName: setup.XMLProjectName,
                  isOverrideQualificationEnabled:
                    setup.isOverrideQualificationEnabled,
                  isOverrideQualificationEnabledNOAH:
                    setup.isOverrideQualificationEnabledNOAH,
                  isOverrideQualificationEnabledDeferredLoan:
                    setup.isOverrideQualificationEnabledDeferredLoan,
                  isOverrideQualificationEnabledVHCB:
                    setup.isOverrideQualificationEnabledVHCB,
                  isOverrideQualificationEnabledNSP:
                    setup.isOverrideQualificationEnabledNSP,
                  isOverrideQualificationEnabledAHP:
                    setup.isOverrideQualificationEnabledAHP,
                  isOverrideQualificationEnabledKCMO:
                    setup.isOverrideQualificationEnabledKCMO,
                  isOverrideQualificationEnabledStLouisFunds:
                    setup.isOverrideQualificationEnabledStLouisFunds,
                  isOverrideQualificationEnabledTCAP:
                    setup.isOverrideQualificationEnabledTCAP,
                  isOverrideQualificationEnabledBOND:
                    setup.isOverrideQualificationEnabledBOND,
                  RDProjectNumber: setup.RDProjectNumber,
                  RDProjectName: setup.RDProjectName,
                  RDBorrowerId: setup.RDBorrowerId,
                  RDNonContinuousParcelsEnabled:
                    setup.RDNonContinuousParcelsEnabled,
                  RDRemoteLocationEnabled: setup.RDRemoteLocationEnabled,
                  RDTroubledOrCritical: setup.RDTroubledOrCritical,
                  loanTransferAmount: setup?.loanTransferAmount,
                  noteRentPayment: setup?.noteRentPayment,
                  icPayment: setup?.icPayment,
                  isRDRAEnabled: setup.isRDRAEnabled,
                  RDTotalRAUnits: setup.RDTotalRAUnits,
                  RD538Loan: setup.RD538Loan,
                  HOMEAffordabilityPeriodStartDate:
                    setup.HOMEAffordabilityPeriodStartDate,
                  HOMEUnitType: setup.HOMEUnitType,
                  HOMEAnnualIncomeType: setup.HOMEAnnualIncomeType,
                  HOMEPropertyType:
                    setup.HOMEPropertyType === 'None'
                      ? null
                      : setup.HOMEPropertyType,
                  HTFUnitType: setup.HTFUnitType,
                  HTFAffordabilityPeriodStartDate:
                    setup.HTFAffordabilityPeriodStartDate,
                  HUDAutoRolloverIncomeAndAssets:
                    setup.HUDAutoRolloverIncomeAndAssets,
                  LIHTCAutoRolloverIncomeAndAssets:
                    setup.LIHTCAutoRolloverIncomeAndAssets,
                  RDAutoRolloverIncomeAndAssets:
                    setup.RDAutoRolloverIncomeAndAssets,
                  HOMEAutoRolloverIncomeAndAssets:
                    setup.HOMEAutoRolloverIncomeAndAssets,
                  HTFAutoRolloverIncomeAndAssets:
                    setup.HTFAutoRolloverIncomeAndAssets,
                  HUDCompleteGRCertsWithoutSigned59As:
                    setup.HUDCompleteGRCertsWithoutSigned59As,
                  NOAHAutoRolloverIncomeAndAssets:
                    setup.NOAHAutoRolloverIncomeAndAssets,
                  DeferredLoanAutoRolloverIncomeAndAssets:
                    setup.DeferredLoanAutoRolloverIncomeAndAssets,
                  VHCBAutoRolloverIncomeAndAssets:
                    setup.VHCBAutoRolloverIncomeAndAssets,
                  NSPAutoRolloverIncomeAndAssets:
                    setup.NSPAutoRolloverIncomeAndAssets,
                  KCMOAutoRolloverIncomeAndAssets:
                    setup.KCMOAutoRolloverIncomeAndAssets,
                  TCAPAutoRolloverIncomeAndAssets:
                    setup.TCAPAutoRolloverIncomeAndAssets,
                  StLouisFundsAutoRolloverIncomeAndAssets:
                    setup.StLouisFundsAutoRolloverIncomeAndAssets,
                  PHAutoRolloverIncomeAndAssets:
                    setup.PHAutoRolloverIncomeAndAssets,
                  BONDAutoRolloverIncomeAndAssets:
                    setup.BONDAutoRolloverIncomeAndAssets,
                  interimCert: setup.interimCert,
                  interimCertNOAH: setup.interimCertNOAH,
                  interimCertDeferredLoan: setup.interimCertDeferredLoan,
                  interimCertVHCB: setup.interimCertVHCB,
                  interimCertNSP: setup.interimCertNSP,
                  interimCertAHP: setup.interimCertAHP,
                  interimCertKCMO: setup.interimCertKCMO,
                  interimCertStLouisFunds: setup.interimCertStLouisFunds,
                  interimCertTCAP: setup.interimCertTCAP,
                  interimCertBOND: setup.interimCertBOND,
                  HUDAnnualRecertMonth: setup.HUDAnnualRecertMonth,
                  LIHTCAnnualRecertMonth: setup.LIHTCAnnualRecertMonth,
                  RDAnnualRecertMonth: setup.RDAnnualRecertMonth,
                  HOMEAnnualRecertMonth: setup.HOMEAnnualRecertMonth,
                  HTFAnnualRecertMonth: setup.HTFAnnualRecertMonth,
                  NOAHAnnualRecertMonth: setup.NOAHAnnualRecertMonth,
                  DeferredLoanAnnualRecertMonth:
                    setup.DeferredLoanAnnualRecertMonth,
                  VHCBAnnualRecertMonth: setup.VHCBAnnualRecertMonth,
                  NSPAnnualRecertMonth: setup.NSPAnnualRecertMonth,
                  AHPAnnualRecertMonth: setup.AHPAnnualRecertMonth,
                  KCMOAnnualRecertMonth: setup.KCMOAnnualRecertMonth,
                  TCAPAnnualRecertMonth: setup.TCAPAnnualRecertMonth,
                  StLouisFundsAnnualRecertMonth:
                    setup.StLouisFundsAnnualRecertMonth,
                  BONDAnnualRecertMonth: setup.BONDAnnualRecertMonth,
                  PHAnnualRecertMonth: setup.PHAnnualRecertMonth,
                  affordableWaitlist: setup.affordableWaitlist,
                  affordableWaitlistStatus: setup.affordableWaitlistStatus,
                  waitlistV2: buildWaitlistV2Payload(
                    setup,
                    affordableDetails.programs,
                  ),
                  RDProjectRentalTypes: (setup.RDProjectRentalTypes ?? [])
                    .map((el) =>
                      typeof el === 'string' ? el : el.value || el.id,
                    )
                    .filter((id) => typeof id === 'string'),
                  reserveAccountRequiredBalance:
                    setup?.reserveAccountRequiredBalance === ''
                      ? null
                      : setup?.reserveAccountRequiredBalance,
                  agencyName: setup?.agencyName,
                  phaCode: setup?.phaCode,
                  PHProgramType: setup?.PHProgramType,
                  PHProjectNumber: setup?.PHProjectNumber,
                  PHProjectSuffix: setup?.PHProjectSuffix,
                  autoSendToPIC: setup?.autoSendToPIC,
                  HUDCertificationPastDueDefinition:
                    setup.HUDCertificationPastDueDefinition,
                  LIHTCCertificationPastDueDefinition:
                    setup.LIHTCCertificationPastDueDefinition,
                  RDCertificationPastDueDefinition:
                    setup.RDCertificationPastDueDefinition,
                  NOAHCertificationPastDueDefinition:
                    setup.NOAHCertificationPastDueDefinition,
                  DeferredLoanCertificationPastDueDefinition:
                    setup.DeferredLoanCertificationPastDueDefinition,
                  VHCBCertificationPastDueDefinition:
                    setup.VHCBCertificationPastDueDefinition,
                  AHPCertificationPastDueDefinition:
                    setup.AHPCertificationPastDueDefinition,
                  NSPCertificationPastDueDefinition:
                    setup.NSPCertificationPastDueDefinition,
                  KCMOCertificationPastDueDefinition:
                    setup.KCMOCertificationPastDueDefinition,
                  StLouisFundsCertificationPastDueDefinition:
                    setup.StLouisFundsCertificationPastDueDefinition,
                  TCAPCertificationPastDueDefinition:
                    setup.TCAPCertificationPastDueDefinition,
                  HOMECertificationPastDueDefinition:
                    setup.HOMECertificationPastDueDefinition,
                  HTFCertificationPastDueDefinition:
                    setup.HTFCertificationPastDueDefinition,
                  PHCertificationPastDueDefinition:
                    setup.PHCertificationPastDueDefinition,
                  BONDCertificationPastDueDefinition:
                    setup.BONDCertificationPastDueDefinition,
                  keyNumber: setup.keyNumber,
                }
              : undefined,

            isSetLeaseEndDateToEndOfMonth:
              propertyData.isSetLeaseEndDateToEndOfMonth,
            insuranceType: propertyData.insuranceType,
            insuranceFeeAmount: propertyData.insuranceFeeAmount,
            insuranceMasterTransactionCodeId:
              propertyData.insuranceMasterTransactionCodeId === 'default'
                ? null
                : propertyData.insuranceMasterTransactionCodeId,

            propertyCollectionSetting: propertyData.propertyCollectionSetting
              ? propertyData.propertyCollectionSetting
              : {},
            mobileAppSetup: propertyData.mobileAppSetup,
            // I'm adding this flag here so Joi can do the check automatically
            // instead of adding checks on the handler
            isCommercialProperty:
              propertyData.hasCommercialFloorPlans === 'ALL',
            secondaryPropertyTypeId: secondaryPropertyType,
            developmentPhase: devPhaseDropDown
              ? propertyData.developmentPhase
              : null,
            ...screeningSubmitPayload,
            stateConfigs: propertyData.stateConfigs,
          },
          integrations: {
            theWorkNumberAffordable: propertyData.theWorkNumberAffordable,
            isConserviceEnabled: propertyData.isConserviceEnabled,
            // New way of handling munibilling access:
            isMunibillingEnabled: propertyData.isMunibillingEnabled,
          },
        };

        const doSubmit =
          !arePropertiesEmpty(data.marketing, [
            'officeHoursDescription',
            'propertyDescription',
            'petPolicyDescription',
            'parkingDetailDescription',
            'amenities',
          ]) &&
          !arePropertiesEmpty(data.property, [
            ...(propertyData.isDomusoActive ? [] : ['domusoPropertyCode']),
            ...(propertyData.fax ? [] : ['faxNumber']),
            ...(propertyData.hasCommercialFloorPlans !== 'ALL'
              ? []
              : ['applicationIncomeMultiplier']),
            'physicalStreetAddress2',
            'leaseRentPercentage',
            'sitePetWeightMax',
            'leaseTerms',
            'cashPrepaidGLCode',
            'phoneNumber',
            'config',
            'isCommercialProperty',
            'secondaryPropertyTypeId',
            'developmentPhase',
            'providerPropertyProfiles',
            'screeningPropertyConfigs',
            'stateConfigs',
          ]) &&
          !(
            masterMeteredUtilitiesOtherSelected &&
            arePropertiesEmpty(data.property.config.masterMeteredUtilities, [
              'electricity',
              'gas',
              'sewer',
              'trash',
              'water',
            ])
          );

        if (doSubmit) {
          setSubmit(true);
          let notificationAttributes;
          let minRentCorrelationId;
          if (isAsyncMinRecalc) {
            minRentCorrelationId = uuid();
            const currentRoutePath = history?.location?.pathname ?? '';
            notificationAttributes = {
              correlationId: minRentCorrelationId,
              currentRoutePath,
              propertyId,
            };
            actions.promptToaster({
              id: minRentInProgressToastId,
              type: 'info',
              className: 'rrt-purple',
              timeOut: 0,
              title: intl.formatMessage(messages.inProgress),
              message: intl.formatMessage(messages.minimumRentUpdateInProgress),
            });
          }
          const throwErrorFromHookFunction = true;
          if (propertyRoleAssignments) {
            assignPropertyRoles();
          }
          if (
            data.property.config.accrueSecurityDepositInterest &&
            accrueSecurityDepositInterestFlag
          ) {
            updateSecurityDepositInterestRates({
              newRates: newInterestRatesToCreate,
              modifiedRates,
            });
          }
          await updateMarketingSection(
            data.marketing,
            intl,
            throwErrorFromHookFunction,
          );
          if (hasPropertyUpdatePermission) {
            // update integrations

            await updateIntegrations(
              data.integrations,
              intl,
              throwErrorFromHookFunction,
            );

            await updateProperty(
              data.property,
              intl,
              throwErrorFromHookFunction,
              notificationAttributes,
            );
            // Recert schedule cancel/backfill runs server-side after commit. Save success
            // is the operator signal; TWC failures are logged server-side without failing save.
          }
          if (isAsyncMinRecalc) {
            // The 200 only means the recalc was queued. Keep the in-progress
            // toast and wait for the socket notification to turn it green (or
            // red). Schedule a fallback so the toast does not hang forever if
            // the notification never arrives.

            // Cancel any fallback still pending from a previous rapid save on
            // the same property so at most one fallback timer is active per
            // property. Without this, a second save would orphan the first
            // timer, which could later fire the timeout toast even after the
            // second save already resolved green.
            if (minimumRentFallbackRef.current) {
              clearTimeout(minimumRentFallbackRef.current);
              minimumRentFallbackRef.current = null;
            }
            clearMinimumRentFallback(propertyId);

            const cancelFallback = () => {
              if (minimumRentFallbackRef.current) {
                clearTimeout(minimumRentFallbackRef.current);
                minimumRentFallbackRef.current = null;
              }
            };
            minimumRentFallbackRef.current = setTimeout(() => {
              minimumRentFallbackRef.current = null;
              clearMinimumRentFallback(propertyId);
              actions.removeToaster(minRentInProgressToastId);
              actions.promptToaster({
                type: 'info',
                title: intl.formatMessage(messages.inProgress),
                message: intl.formatMessage(messages.minimumRentRecalcTimeout),
              });
            }, MINIMUM_RENT_RECALC_TIMEOUT_MS);
            registerMinimumRentFallback(
              propertyId,
              cancelFallback,
              minRentCorrelationId,
            );
          } else {
            actions.removeToaster(minRentInProgressToastId);
            actions.promptToaster({
              title: intl.formatMessage(messages.success),
              message: intl.formatMessage(messages.successBody),
            });
          }
          setEditMode(false);
          setSubmit(false);
        }
      } catch (e) {
        actions.removeToaster(minRentInProgressToastId);
        actions.promptToaster({
          type: 'error',
          title: intl.formatMessage(messages.error),
          message: intl.formatMessage(messages.errorBody),
        });
        setEditMode(false);
        setSubmit(false);
      }
    } else {
      setEditMode(true);
    }
  };

  const onCancel = () => {
    setMarketingData(marketingInfo);
    setPropertyData(propertyDetails);
    setLeaseTerms(propertyLeaseTermsRef.current);
    setEditMode(false);
  };
  const isFormInvalid = useIsFormInvalid({
    propertyData,
    originalPropertyDetails: propertyDetails,
    marketingData,
    affordableDetails,
    passbookRate,
    isNewServiceOn,
    riskMgmtAssetProtectAdmin,
    interestRatesAreValid,
  });

  const onSave = () => {
    if (hasNewHUDContractNumber(propertyData?.setup, propertyDetails?.setup)) {
      setShowAddContractConfirmation(true);
      return;
    }
    onEdit();
  };

  const onAddContractConfirm = () => {
    setShowAddContractConfirmation(false);
    onEdit();
  };

  return (
    <>
      <AddContractConfirmationModal
        open={showAddContractConfirmation}
        onClose={() => setShowAddContractConfirmation(false)}
        onConfirm={onAddContractConfirm}
        intl={intl}
      />
      <BankInformationModal
        show={showBankInformationModal}
        onHide={() => setShowBankInformationModal(false)}
        intl={intl}
        promptToaster={actions.promptToaster}
        organizationId={organizationId}
        propertyId={propertyId}
        propertyBankAccounts={propertyDetails.propertyBankAccounts}
      />
      <DocumentTitle title={intl.formatMessage(messages.title)}>
        <Box sx={{ maxWidth: 1400, margin: 'auto' }}>
          {!isPropertyHomeTab && (
            <Button
              variant={'text'}
              onClick={() => history.goBack()}
              startIcon={
                <ExpandLessIcon sx={{ transform: 'rotate(-90deg)' }} />
              }
              sx={{
                visibility:
                  history && window.history.length > 1 ? 'visible' : 'hidden',
              }}
            >
              <FormattedMessage {...messages.goBack} />
            </Button>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Stack
                direction={{ xs: 'column', sm: 'row-reverse' }}
                alignItems={{ sm: 'center' }}
                justifyContent={{ sm: 'space-between' }}
                spacing={4}
                sx={{ marginBottom: 2, maxWidth: { md: 'calc(50% - 8px)' } }}
              >
                <Image
                  alt={marketingData.name}
                  src={marketingData.propertyLogoLink}
                  sx={{
                    width: {
                      xs: 'calc(100vw - 32px)',
                      sm: 'unset',
                    },
                    maxWidth: {
                      sm: 250,
                    },
                    padding: 1,
                    borderRadius: 2,
                    backgroundColor: 'background.paper',
                  }}
                  initialSX={{
                    width: {
                      sm: 250,
                    },
                    height: {
                      xs: 100,
                      sm: 'unset',
                    },
                  }}
                />
                <Typography
                  variant={'headlineMedium'}
                  component="h1"
                  loading={!marketingData.name}
                  minWidth={{ sm: 200 }}
                >
                  {marketingData.name}
                </Typography>
              </Stack>
            </Grid>

            <Grid item xs={12} md={6}>
              <ElementWithPermissions
                scope={['property-update', 'property-update-marketing-gallery']}
              >
                <MarketingDetails
                  marketingInfo={marketingData}
                  editMode={editMode}
                  onChange={onMarketingDataChange}
                />
              </ElementWithPermissions>
            </Grid>
            <Grid item xs={12} md={6}>
              <ElementWithPermissions scope={['property-update']}>
                <BasicDetails
                  propertyDetails={propertyData}
                  orgPropUrl={orgPropUrl}
                  editMode={editMode}
                  onChange={onPropertyDataChange}
                  states={states}
                  intl={intl}
                  promptToaster={actions.promptToaster}
                  organizationId={organizationId}
                  propertyRoles={propertyRoles}
                  roleAssignments={roleAssignments}
                  setRoleAssignments={setRoleAssignments}
                  handleAssignmentChange={handleAssignmentChange}
                  secondaryPropertyTypes={secondaryPropertyTypes}
                  secondaryPropertyType={secondaryPropertyType}
                  handleSecondaryPropertyTypeChange={
                    handleSecondaryPropertyTypeChange
                  }
                />
              </ElementWithPermissions>
            </Grid>
            {isAffordableSetupShown && (
              <Grid item xs={12} md={6}>
                <ElementWithPermissions scope={['property-update']}>
                  <AffordableSetup
                    promptToaster={actions.promptToaster}
                    editMode={editMode}
                    onChange={onPropertyDataChange}
                    intl={intl}
                    {...affordableSetupValues}
                    {...affordableDetails}
                    stateConfigs={propertyData.stateConfigs}
                  />
                </ElementWithPermissions>
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <ElementWithPermissions scope={['property-update']}>
                <LeaseSetup
                  propertyDetails={propertyData}
                  editMode={editMode}
                  onChange={onPropertyDataChange}
                  leaseTerms={leaseTerms}
                  setLeaseTerms={setLeaseTerms}
                  propertyLeaseTermsRef={propertyLeaseTermsRef}
                  masterLeaseTerms={masterLeaseTerms}
                  prorateMethods={prorateMethods}
                  moveOutProrateMethods={moveOutProrateMethods}
                  firstOfMonthPostEnabled={
                    isMltStatusLoading ? false : enabledStatusMLT
                  }
                  mltPostingError={errorMLTValidation}
                  mltPosting={isPostingMLT}
                  postMLT={postMLT}
                  isLeaseExpirationLimitsActive={
                    propertyData.isLeaseExpirationLimitsActive
                  }
                  isAutoChargeMtmFees={propertyData?.config?.autoChargeMtmFees}
                  organizationId={organizationId}
                  toasterFn={actions.promptToaster}
                  history={history}
                  intl={intl}
                  propertyDetailsIsLoading={propertyDetailsIsLoading}
                  locale={locale}
                  masterMeteredUtilitiesOtherSelected={
                    masterMeteredUtilitiesOtherSelected
                  }
                  setMasterMeteredUtilitiesOtherSelected={
                    setMasterMeteredUtilitiesOtherSelected
                  }
                  {...{
                    securityDepositInterestRates,
                    setSecurityDepositInterestRates,
                    editableRates,
                    modifiedRates,
                    setModifiedRates,
                    newInterestRatesToCreate,
                    setNewInterestRatesToCreate,
                    securityDepositInterestRatesIsLoading:
                      securityDepositInterestRatesIsLoading || submit,
                  }}
                />
              </ElementWithPermissions>
            </Grid>
            <Grid item xs={12} md={6}>
              <ElementWithPermissions scope={['property-update']}>
                <IntegrationsSetup
                  propertyDetails={propertyData}
                  organization={{
                    ...userOrganization,
                    defaultCollectionAgencyId: defaultCollectionAgency,
                  }}
                  editMode={editMode}
                  onChange={onPropertyDataChange}
                  intl={intl}
                  organizationId={organizationId}
                  propertyId={propertyId}
                  isLoading={propertyDetailsIsLoading}
                  selectedPropertyTransactionCodes={
                    selectedPropertyTransactionCodes
                  }
                />
              </ElementWithPermissions>
            </Grid>
            <Grid item xs={12} md={6}>
              <ElementWithPermissions scope={['property-update']}>
                <TwoWayTextingSetup
                  editMode={editMode}
                  config={propertyData.config}
                  isTwoWayCommunicationActive={
                    propertyData.isTwoWayCommunicationActive
                  }
                  showRecertTextReminders={isAffordableSetupShown}
                  onChange={onPropertyDataChange}
                />
              </ElementWithPermissions>
            </Grid>
            {riskMgmtAssetProtectAdmin && (
              <Grid item xs={12} md={6}>
                <ElementWithPermissions scope={['property-update']}>
                  <InsuranceSetup
                    propertyDetails={propertyData}
                    editMode={editMode}
                    onChange={onPropertyDataChange}
                    intl={intl}
                    selectedPropertyTransactionCodes={
                      selectedPropertyTransactionCodes
                    }
                  />
                </ElementWithPermissions>
              </Grid>
            )}
            {showMobileAppSetupPanel && (
              <Grid item xs={12} md={6}>
                <ElementWithPermissions scope={['property-update']}>
                  <MobileAppSetup
                    editMode={editMode}
                    config={propertyData.config}
                    onChange={onPropertyDataChange}
                  />
                </ElementWithPermissions>
              </Grid>
            )}
          </Grid>
          <Stack
            spacing={2}
            direction={'row'}
            justifyContent={'flex-end'}
            flexWrap={'wrap'}
            sx={{
              marginY: 2,
            }}
          >
            <ElementWithPermissions scope={['bank-accounts-view-edit']} hidden>
              <Button
                variant={'shout'}
                onClick={() => setShowBankInformationModal(true)}
              >
                {intl.formatMessage(messages.viewEditBankInformation)}
              </Button>
            </ElementWithPermissions>

            {editMode ? (
              <Stack
                spacing={2}
                direction={'row'}
                sx={{ mt: { xs: '16px !important', sm: 'unset !important' } }}
              >
                <ElementWithPermissions
                  scope={[
                    'property-update',
                    'property-update-marketing-gallery',
                  ]}
                >
                  <SubmitButton
                    data-testid={'cancel-btn'}
                    onClick={onCancel}
                    // Cancel discards the edits and reloads the fetched values,
                    // so form validity has no bearing on it. Gating it meant an
                    // invalid page trapped the user in edit mode with no way out.
                    disabled={submit}
                    isSubmitting={submit}
                  >
                    Cancel
                  </SubmitButton>
                  <SubmitButton
                    data-testid={'save-btn'}
                    variant={'primary'}
                    onClick={onSave}
                    disabled={isFormInvalid || submit}
                    isSubmitting={submit}
                    startIcon={<FloppyIcon />}
                  >
                    Save Changes
                  </SubmitButton>
                </ElementWithPermissions>
              </Stack>
            ) : (
              <ElementWithPermissions
                scope={['property-update', 'property-update-marketing-gallery']}
              >
                <Button
                  data-testid={'edit-btn'}
                  type="button"
                  variant={'primary'}
                  onClick={onEdit}
                  disabled={
                    marketingSectionIsLoading && propertyDetailsIsLoading
                  }
                  startIcon={<PencilIcon />}
                >
                  Edit
                </Button>
              </ElementWithPermissions>
            )}
          </Stack>
        </Box>
      </DocumentTitle>
    </>
  );
};

const InjectedViewProperty = injectIntl(ViewProperty);

export const mapStateToProps = ({
  app: { currentUser, selectedProperty, selectedPropertyTransactionCodes },
  languageProvider,
}: any): any => {
  return {
    userId: currentUser?.user?.id ?? '',
    organizationId: currentUser.user.organizationId,
    userPermissions: currentUser.permissions,
    userOrganization: currentUser.user.organization,
    selectedProperty,
    selectedPropertyTransactionCodes,
    currentUserOrg: currentUser.user.organization,
    locale: languageProvider.locale ?? 'en_US',
  };
};

export const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
  actions: bindActionCreators(
    {
      promptToaster,
      removeToaster,
      selectProperty,
      updateCurrentUserSelectedProperty,
    },
    dispatch,
  ),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(InjectedViewProperty);
